import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, ScoreRing, SkeletonList, QueryError, UnlockHint } from '../../../../components/ui';
import { api } from '../../../../lib/api';
import { whoseModel, whyNoPredictions, type ClientModelStanding } from '../../../../lib/client-health-predictor';

export function ClientHealthWidget() {
  const navigate = useNavigate();
  const basePath = useBasePath();

  const clientHealth = useQuery<{ items: Array<{ client_external_id: string; client_name: string; churn_probability: number; top_drivers: Array<{ label: string }> | null }>; summary: { highRisk: number }; model?: ClientModelStanding | null }>({
    queryKey: ['client-health-predictions'],
    queryFn: async () => {
      return api('/v1/client-health-predictor/predictions');
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });

  const hi = clientHealth.data?.items?.filter(c => (c.churn_probability ?? 0) >= 0.5).slice(0, 4) ?? [];

  return (
    <DataCard
      title="Client Health"
      subtitle={`Top at-risk clients by churn probability${whoseModel(clientHealth.data?.model) ? `, from ${whoseModel(clientHealth.data?.model)}` : ''}`}
      badge={clientHealth.data?.summary?.highRisk ? <StatusBadge status="critical" label={`${clientHealth.data.summary.highRisk} at risk`} size="xs" /> : undefined}
    >
      {clientHealth.isLoading ? <SkeletonList rows={3} /> : clientHealth.isError ? <QueryError error={clientHealth.error} onRetry={() => clientHealth.refetch()} compact /> : hi.length === 0 ? (
        <UnlockHint
          need="crm"
          unlocks="and Waypoint will score every client's churn risk."
          connectedFallback={clientHealth.data?.items?.length ? 'No high-risk clients right now.' : whyNoPredictions('churn', clientHealth.data?.model)}
        />
      ) : (
        <div className="space-y-1.5">
          {hi.map(c => (
            <div key={c.client_external_id}
              onClick={() => navigate(`${basePath}/clients`)}
              className="flex items-center justify-between rounded-lg bg-surface-1 px-3 py-2 cursor-pointer hover:bg-surface-2 transition-colors">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-ink">{c.client_name}</p>
                {Array.isArray(c.top_drivers) && c.top_drivers[0] && (
                  <p className="text-2xs text-ink-dim truncate">{c.top_drivers[0].label}</p>
                )}
              </div>
              <ScoreRing score={Math.round((c.churn_probability ?? 0) * 100)} size={32} strokeWidth={3} colorScale="risk" />
            </div>
          ))}
        </div>
      )}
    </DataCard>
  );
}
